import { styles as buttonStyles } from '@orn/shared-web/components/Button/Button';
import { LockIcon, SparkleIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import { Link } from 'react-router-dom';

import { formatPeriodEnd } from '../../components/settings/BillingSettings/billingSummary';
import { SettingsTab, settingsTabHref } from '../settings/settingsTabs';

import PauseGlyph from './PauseGlyph';

import type { AssistantMembershipPause } from '../../hooks/assistant/useAssistantMembershipPause';

/** Questions the paused state shows as what a membership unlocks. */
const LOCKED_EXAMPLE_QUESTIONS = [
  'How much did I spend on dining last month?',
  'Which subscriptions renew this week?',
  'Explain the alert I just got',
];

interface IPausedCopy {
  heading: string;
  status: string;
  /** The Billing link's label, or `null` when the viewer has no billing action to take. */
  action: string | null;
}

const pausedCopy = (pause: AssistantMembershipPause): IPausedCopy => {
  switch (pause.kind) {
    case 'lapsed':
      return {
        heading: 'Restart Mantle to keep asking',
        status:
          pause.endedAt !== null
            ? `Your membership ended ${formatPeriodEnd(pause.endedAt)}.`
            : 'Your membership isn’t active.',
        action: 'Restart membership',
      };
    case 'neverStarted':
      return {
        heading: 'Start Mantle to keep asking',
        status: 'Your membership isn’t active.',
        action: 'Start membership',
      };
    default:
      return {
        heading: 'The assistant is paused',
        status: 'Your household’s Mantle membership isn’t active.',
        action: null,
      };
  }
};

interface IAssistantMembershipPausedProps {
  pause: AssistantMembershipPause;
}

/**
 * The assistant pane's body while the household holds no active membership:
 * what is paused, what a membership unlocks, and — when the viewer can act on
 * it — a link to the Billing tab. Replaces the transcript rather than sitting
 * over it, since the server refuses every turn the history could invite.
 */
const AssistantMembershipPaused = ({ pause }: IAssistantMembershipPausedProps) => {
  const { heading, status, action } = pausedCopy(pause);

  return (
    <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-7 py-10 text-center">
      <div className="bg-primary border-secondary size-22 relative flex items-center justify-center rounded-[28px] border shadow-md">
        <SparkleIcon aria-hidden="true" className="text-brand-solid size-9" />
        <div className="bg-warning-secondary text-warning-primary border-bg-app absolute -bottom-1.5 -right-1.5 flex size-8 items-center justify-center rounded-full border-[3px]">
          <PauseGlyph className="size-3.5" />
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="text-primary text-display-xs font-semibold">{heading}</div>
        <div className="text-secondary text-reg">
          {status} Your accounts and chat history are saved. They’ll be here when you’re back.
        </div>
      </div>

      <div className="flex w-full flex-col gap-2 text-left">
        <div className="text-tertiary text-xs font-semibold uppercase tracking-wider">
          With a membership, you can ask
        </div>
        {LOCKED_EXAMPLE_QUESTIONS.map((question) => (
          <div
            key={question}
            className="bg-primary border-secondary text-secondary flex items-center gap-2.5 rounded-xl border px-3.5 py-3 text-sm"
          >
            <LockIcon aria-hidden="true" className="text-quaternary size-4 shrink-0" />
            {question}
          </div>
        ))}
      </div>

      {action !== null && (
        <Link
          to={settingsTabHref(SettingsTab.Billing)}
          className={cx(
            buttonStyles.common.root,
            buttonStyles.sizes.lg.root,
            buttonStyles.colors.primary.root,
            'w-full'
          )}
        >
          {action}
        </Link>
      )}
    </div>
  );
};

export default AssistantMembershipPaused;
